import { useRef, useState } from 'react'
import styled from 'styled-components'

import { colors, font, tablet, Button, InlineLink, Loadable, ModalLegacy as Modal } from '@scouts/ui'

import config from '@/config'
import { hexToRgbA } from '@/helpers'
import { DraftAgreement } from '@/models'
import { useAccountant } from '@/store/accountant'
import { useCreateUserAgreement } from '@/store/user-agreement'

import { useScrollPosition } from '@/hooks/use-scroll-position'

import { AccountantAvatar } from '../accountant/AccountantAvatar'
import ServiceAgreementBody from '../ui/ServiceAgreementBody'

interface SignPendingAgreementModalProps {
    agreement: DraftAgreement
    onAgreementSign: () => void
}

export const SignPendingAgreementModal = ({ agreement, onAgreementSign }: SignPendingAgreementModalProps) => {
    const [isSignaturePending, setSignaturePending] = useState(false)
    const { accountantFullName } = useAccountant()

    const createUserAgreement = useCreateUserAgreement({ userAgreementType: agreement.type })

    const { agreementText } = agreement
    const bodyRef = useRef(null)

    const signAgreement = () => {
        setSignaturePending(true)
        // @TODO:
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ;(createUserAgreement() as any).then(onAgreementSign).finally(() => setSignaturePending(false))
    }

    const { scrollPosition: scrollTopPosition } = useScrollPosition({
        ref: bodyRef,
    })

    return (
        <Modal isWide>
            <Header>
                <HeaderColumn>
                    <HeaderTitle>{accountantFullName} será tu asesor</HeaderTitle>
                    <HeaderSubtext>
                        Para seguir usando tu Pack, debes aceptar las nuevas condiciones, ¿les echas un ojo 👀? Si
                        tienes alguna duda, no te preocupes, ¡aquí estamos!
                    </HeaderSubtext>
                    <HeaderSubtext>
                        <InlineLink rel="noopener noreferrer" target="_blank" href={config.termsUrl}>
                            Términos y condiciones
                        </InlineLink>
                    </HeaderSubtext>
                    <HeaderSubtext>
                        <InlineLink rel="noopener noreferrer" target="_blank" href={config.privacyUrl}>
                            Política de privacidad
                        </InlineLink>
                    </HeaderSubtext>
                    <HeaderSubtext>Acuerdo de servicio, que verás abajo:</HeaderSubtext>
                </HeaderColumn>
                <HeaderColumn>
                    <AccountantAvatar />
                </HeaderColumn>
            </Header>

            <Body withBottomFade={scrollTopPosition === 0} ref={bodyRef}>
                {!agreementText && <Loadable isLoading />}
                {agreementText && <ServiceAgreementBody source={agreementText} />}
            </Body>

            <Footer>
                <ActionContainer>
                    <Button isLoading={isSignaturePending} onClick={signAgreement} size="large">
                        Acepto los términos del servicio
                    </Button>
                </ActionContainer>
            </Footer>
        </Modal>
    )
}

const Header = styled.div`
    line-height: 1.5;
    display: flex;
    justify-content: space-between;
    padding: 18px 52px 18px 24px;
    background: ${colors.surface.highlight};

    ${tablet`
        padding: 36px 48px;

    `};
`

const HeaderColumn = styled.div``

const HeaderTitle = styled.h2`
    font-size: ${font.normal};
    font-weight: ${font.weight.medium};
    margin-bottom: 12px;

    ${tablet`
        font-size: ${font.large};
        margin-bottom: 18px;
    `}
`

const HeaderSubtext = styled.p`
    font-size: ${font.small};

    &:not(:last-child) {
        margin-bottom: 6px;
    }

    ${tablet`
        font-size: ${font.normal};
    `}
`

const Body = styled.div<{ withBottomFade?: boolean }>`
    overflow: auto;
    position: relative;
    max-height: 50vh;
    padding: 12px 24px;

    ${tablet`
        padding: 12px 48px;
    `};

    &:after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 200px;
        background: linear-gradient(to bottom, ${hexToRgbA(colors.surface.base, 0)}, ${colors.surface.base} 90%);
        pointer-events: none;

        visibility: ${({ withBottomFade }) => (withBottomFade ? 'visible' : 'hidden')};
    }
`

const Footer = styled.div`
    padding: 18px 24px;
    background: ${colors.surface.highlight};

    ${tablet`
        padding: 24px 48px;
    `};
`

const ActionContainer = styled.div`
    display: flex;
    justify-content: flex-end;
`
